import {zodResolver} from '@hookform/resolvers/zod';
import React from 'react';
import {useForm} from 'react-hook-form';
import {Keyboard, View} from 'react-native';
import * as z from 'zod';

import {LoadingButton} from '../../components/Button';
import {ErrorMessage} from '../../components/Form/ErrorMessage';
import {Input} from '../../components/Form/Input';
import {InputContainer} from '../../components/Form/InputContainer';
import {ValidatedField} from '../../components/Form/ValidatedField';
import {inputIconProps} from '../../components/Form/util';
import {Text} from '../../components/Typography';
import {ErrorMessages, Strings, texts} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {MailIcon} from '../../icons';
import {isHttpError, userApi} from '../../lib/api/apis';
import {useBackPress} from '../../lib/hooks/useBackPress';
import NavigationService from '../../lib/utils/NavigationService';
import type {RootStackScreenProps} from '../../navigators/RootStackNavigator';
import {centerText} from '../../styles';
import {LogoHeader, LogoTitle} from '../components';
import {KeyboardAvoidingLayout, TitleContainer} from '../components/composable';
import {authStyles} from './styles';

const schema = z.object({
  email: z
    .string()
    .nonempty(ErrorMessages.REQUIRED_EMAIL)
    .email(ErrorMessages.INVALID_EMAIL)
    .transform(val => val.trim()),
});

type ForgotPasswordValues = z.infer<typeof schema>;

export interface Props extends ForgotPasswordValues {}

interface ScreenProps
  extends RootStackScreenProps<typeof Screens.ForgotPassword> {}

export const ForgotPasswordScreen: React.FC<ScreenProps> = ({
  route,
  navigation,
}) => {
  useBackPress(() => {
    navigation.goBack();
    return true;
  });

  const defaultValues: ForgotPasswordValues = {
    email: route?.params?.email ?? '',
  };

  const {
    control,
    handleSubmit,
    setError,
    formState: {isSubmitting, errors},
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });

  const onSubmit = React.useCallback(
    async ({email}: ForgotPasswordValues) => {
      Keyboard.dismiss();

      await userApi
        .requestPasswordReset({
          userRequestResetPasswordDto: {email},
        })
        .catch(error => {
          if (!isHttpError(error)) throw error;

          throw new Error(ErrorMessages.NETWORK_ERROR);
        });

      NavigationService.instance()?.navigate(Screens.PasswordResetSent, {
        email,
      });
    },
    [navigation],
  );

  return (
    <KeyboardAvoidingLayout>
      <View style={authStyles.innerContainer}>
        <LogoHeader>
          <TitleContainer>
            <LogoTitle>{texts.screens.forgotPassword.title}</LogoTitle>
          </TitleContainer>
        </LogoHeader>

        <Text variant="s" style={centerText}>
          {texts.screens.forgotPassword.subtitle}
        </Text>

        <View style={authStyles.bodyContainer}>
          <ValidatedField {...{control}} name="email">
            {props => (
              <InputContainer invalid={!!errors.email}>
                <MailIcon {...inputIconProps} />

                <Input
                  keyboardType="email-address"
                  placeholder={Strings.EMAIL_ADDRESS}
                  {...props}
                />
              </InputContainer>
            )}
          </ValidatedField>

          {errors.root?.message && (
            <ErrorMessage>{errors.root?.message}</ErrorMessage>
          )}

          <LoadingButton
            loading={isSubmitting}
            disabled={isSubmitting}
            onPress={handleSubmit(values =>
              onSubmit(values).catch(e => {
                setError('root', {message: e.message});
              }),
            )}>
            {Strings.RESET_PASSWORD}
          </LoadingButton>
        </View>
      </View>
    </KeyboardAvoidingLayout>
  );
};
